/**
 * SB Single Keyword Campaigns
 * One campaign per keyword, aligned with SbGroupKeywordCampaigns design
 */

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Alert, Spinner, Badge } from 'react-bootstrap';
import { FaRocket, FaCheckCircle, FaFileAlt, FaImage, FaTag, FaFont, FaDownload, FaTrash } from 'react-icons/fa';
import Layout from './Layout';
import SbSingleKeywordCampaignAPI from '../services/SbSingleKeywordCampaignAPI';
import '../assets/css/SbGroupKeywordCampaigns.css';

const SbSingleKeywordCampaigns = () => {
    // Files
    const [keywordsFile, setKeywordsFile] = useState(null);
    const [productsFile, setProductsFile] = useState(null);
    const [negativeFile, setNegativeFile] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [creativeFile, setCreativeFile] = useState(null);
    const [creativePreview, setCreativePreview] = useState(null);

    // Config
    const [headline, setHeadline] = useState('');
    const [dailyBudget, setDailyBudget] = useState(15);
    const [defaultBid, setDefaultBid] = useState(1.0);
    const [campaignState, setCampaignState] = useState('paused');
    const [brandEntityId, setBrandEntityId] = useState('');
    const [isManualBrandEntry, setIsManualBrandEntry] = useState(false);
    const [selectedCampaignIndices, setSelectedCampaignIndices] = useState(new Set());
    const [bulkAction, setBulkAction] = useState('');
    const [availableBrands, setAvailableBrands] = useState([]);

    // UI State
    const [loading, setLoading] = useState(false);
    const [processingFiles, setProcessingFiles] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [step, setStep] = useState(1);
    const [generatedCampaigns, setGeneratedCampaigns] = useState([]);

    const getProfileId = () => localStorage.getItem('selectedAccountValue') || null;

    useEffect(() => {
        fetchBrands();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchBrands = async () => {
        const profileId = getProfileId();
        if (profileId) {
            try {
                const response = await SbSingleKeywordCampaignAPI.getBrands(profileId);
                if (response.success && response.brands) {
                    setAvailableBrands(response.brands);
                    if (response.brands.length > 0 && !brandEntityId) {
                        setBrandEntityId(response.brands[0].brandEntityId);
                    }
                }
            } catch (err) {
                console.error("Error fetching brands:", err);
            }
        }
    };

    // File Handlers
    const handleKeywordsFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) setKeywordsFile(file);
    };

    const handleProductsFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) setProductsFile(file);
    };

    const handleNegativeFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) setNegativeFile(file);
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setLogoPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleCreativeChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCreativeFile(file);
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onloadend = () => setCreativePreview(reader.result);
                reader.readAsDataURL(file);
            } else if (file.type.startsWith('video/')) {
                setCreativePreview(URL.createObjectURL(file));
            }
        }
    };

    // Generate Campaigns - ONE per keyword
    const handleGenerateClick = async () => {
        if (!brandEntityId) {
            setError('Please select a Brand Entity ID');
            return;
        }
        if (!keywordsFile || !productsFile || !logoFile) {
            setError('Please upload required files: Keywords CSV, ASINs CSV, and logo.');
            return;
        }

        setProcessingFiles(true);
        setError(null);

        try {
            const response = await SbSingleKeywordCampaignAPI.uploadFiles(keywordsFile, productsFile, logoFile, creativeFile, negativeFile);

            if (response.success) {
                const loadedKeywords = response.data.keywords || [];
                const loadedProducts = response.data.products || [];
                const loadedNegatives = response.data.negatives || [];

                const initialSelectedProducts = loadedProducts.slice(0, 10);
                if (initialSelectedProducts.length < 3) {
                    setError(`Warning: Only ${initialSelectedProducts.length} ASINs were found. At least 3 ASINs are required.`);
                }

                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                const startDateStr = tomorrow.toISOString().slice(0, 10);

                // ONE campaign per keyword
                const campaigns = loadedKeywords.map((kw, idx) => ({
                    id: idx,
                    keywordText: kw.keyword || kw.Keyword || kw.term || '',
                    matchType: kw.matchType || kw['Match Type'] || 'BROAD',
                    bid: kw.bid || kw.Bid || defaultBid,
                    asins: initialSelectedProducts.map(p => p.asin || p.ASIN || p),
                    brandEntityId: brandEntityId,
                    budget: dailyBudget,
                    name: `SB_SK_${(kw.keyword || kw.Keyword || '').replace(/\s+/g, '_').substring(0, 30)}_${Date.now()}`,
                    headline: headline || `Descubre ${kw.keyword || kw.Keyword || ''}`,
                    costType: 'CPC',
                    goal: 'AWARENESS',
                    portfolioId: '',
                    smartDefault: 'ON',
                    startDate: startDateStr,
                    status: campaignState.toUpperCase(),
                    logoName: logoFile.name || '',
                    creativeName: creativeFile.name || 'Auto-generado',
                    negatives: loadedNegatives
                }));

                setGeneratedCampaigns(campaigns);
                setStep(2);
            } else {
                setError(response.error || 'Error processing files');
            }
        } catch (err) {
            setError(err.message || 'Error generating campaigns');
        } finally {
            setProcessingFiles(false);
        }
    };

    // Bulk Actions
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedCampaignIndices(new Set(generatedCampaigns.map((_, i) => i)));
        } else {
            setSelectedCampaignIndices(new Set());
        }
    };

    const handleSelectRow = (idx) => {
        const newSet = new Set(selectedCampaignIndices);
        if (newSet.has(idx)) newSet.delete(idx);
        else newSet.add(idx);
        setSelectedCampaignIndices(newSet);
    };

    const updateCampaignRow = (idx, field, value) => {
        setGeneratedCampaigns(prev => prev.map((c, i) => i === idx ? { ...c, [field] : value } : c));
    };

    const handleBulkActionApply = () => {
        if (!bulkAction || selectedCampaignIndices.size === 0) return;
        const indicesToUpdate = Array.from(selectedCampaignIndices);

        setGeneratedCampaigns(prev => prev.map((c, i) => {
            if (!indicesToUpdate.includes(i)) return c;
            if (bulkAction === 'PAUSED') return { ...c, status: 'PAUSED' };
            if (bulkAction === 'ENABLED') return { ...c, status: 'ENABLED' };
            return c;
        }));
        setBulkAction('');
    };

    const handleDeleteSelected = () => {
        setGeneratedCampaigns(prev => prev.filter((_, i) => !selectedCampaignIndices.has(i)));
        setSelectedCampaignIndices(new Set());
    };

    const handleExportCSV = () => {
        const headers = ['Campaign Name', 'Keyword', 'Match Type', 'Bid', 'Budget', 'Status', 'Brand Entity ID', 'Headline'];
        const rows = generatedCampaigns.map(c => [c.name, c.keywordText, c.matchType, c.bid, c.budget, c.status, c.brandEntityId, c.headline]);
        const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'sb_single_keyword_campaigns.csv';
        a.click();
    };

    // Create All Campaigns
    const handleCreateAllCampaigns = async () => {
        const profileId = getProfileId();
        if (!profileId) {
            setError('Please select an account profile');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const campaignsToCreate = selectedCampaignIndices.size > 0 ? generatedCampaigns.filter((_, i) => selectedCampaignIndices.has(i)) : generatedCampaigns;

            const results = [];
            for (const camp of campaignsToCreate) {
                try {
                    const response = await SbSingleKeywordCampaignAPI.createCampaign({
                        profileId,
                        campaignName: camp.name,
                        dailyBudget: camp.budget,
                        startDate: camp.startDate,
                        endDate: '',
                        defaultBid: camp.bid,
                        state: camp.status.toLowerCase(),
                        brandEntityId: camp.brandEntityId,
                        headline: camp.headline,
                        keyword: camp.keywordText,
                        matchType: camp.matchType,
                        productAsins: camp.asins,
                        negatives: camp.negatives
                    });
                    results.push({ name: camp.name, success: response.success, id: response.data.campaignId, message: response.message });
                } catch (err) {
                    results.push({ name: camp.name, success: false, message: err.message });
                }
            }

            setResult({ success: true, results });
            setStep(3);
        } catch (err) {
            setError(err.message || 'Error creating campaigns');
        } finally {
            setLoading(false);
        }
    };

    const StatusBadge = ({ status }) => {
        const bg = status === 'ENABLED' ? 'success' : status === 'PAUSED' ? 'warning' : 'secondary';
        return <Badge bg={bg}>{status}</Badge>;
    };

    return (
        <Layout>
            <Container fluid className="sb-keyword-campaigns-page p-4">
                {/* Header */}
                <Row className="mb-4">
                    <Col>
                        <h2><FaRocket className="me-2" />SB Single Keyword Campaigns</h2>
                        <p className="text-muted">One campaign per keyword. Ideal for granular budget control.</p>
                    </Col>
                </Row>

                {/* Alerts */}
                {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
                {result && result.success && step < 3 && <Alert variant="success">{result.message}</Alert>}

                {/* Step 1: Setup */}
                {step === 1 && (
                    <>
                        {/* Config Section */}
                        <div className="config-section-card">
                            <Row className="g-3">
                                <Col md={4}>
                                    <label className="text-white mb-2 fw-bold">
                                        <FaTag className="config-label-icon" /> Brand Entity ID
                                    </label>
                                    <Form.Select
                                        className="custom-select" value={isManualBrandEntry ? 'MANUAL' : brandEntityId}
                                        onChange={e => {
                                            if (e.target.value === 'MANUAL') {
                                                setIsManualBrandEntry(true);
                                                setBrandEntityId('');
                                            } else {
                                                setIsManualBrandEntry(false);
                                                setBrandEntityId(e.target.value);
                                            }
                                        }}
                                    >
                                        <option value="">Select Brand...</option>
                                        {availableBrands.map((b, i) => (
                                            <option key={i} value={b.brandEntityId}>{b.brandEntityId} ({b.brandRegistryName || 'Unknown'})</option>
                                        ))}
                                        <option value="MANUAL">Enter manually...</option>
                                    </Form.Select>
                                    {isManualBrandEntry && (
                                        <Form.Control type="text" className="custom-input mt-2" placeholder="ENTITY..." value={brandEntityId} onChange={e => setBrandEntityId(e.target.value)} />
                                    )}
                                </Col>
                                <Col md={2}>
                                    <label className="text-white mb-2 fw-bold">Budget ($)</label>
                                    <Form.Control type="number" className="custom-input" value={dailyBudget} onChange={e => setDailyBudget(parseFloat(e.target.value))} min={1} />
                                </Col>
                                <Col md={2}>
                                    <label className="text-white mb-2 fw-bold">Bid ($)</label>
                                    <Form.Control type="number" className="custom-input" value={defaultBid} onChange={e => setDefaultBid(parseFloat(e.target.value))} min={0.02} step={0.01} />
                                </Col>
                                <Col md={2}>
                                    <label className="text-white mb-2 fw-bold">Status</label>
                                    <Form.Select className="custom-select" value={campaignState} onChange={e => setCampaignState(e.target.value)}>
                                        <option value="paused">Paused</option>
                                        <option value="enabled">Enabled</option>
                                    </Form.Select>
                                </Col>
                                <Col md={12}>
                                    <label className="text-white mb-2 fw-bold"><FaFont className="config-label-icon" /> Headline (Optional)</label>
                                    <Form.Control type="text" className="custom-input" placeholder="[Keyword] will be used by default" value={headline} onChange={e => setHeadline(e.target.value)} />
                                </Col>
                            </Row>
                        </div>

                        {/* Upload Grid */}
                        <div className="upload-grid">
                            {/* Keywords */}
                            <div className={`upload-card-new ${keywordsFile ? 'has-file' : ''}`}>
                                <div className="card-required-badge">Required</div>
                                <div className="upload-icon"><FaFileAlt /></div>
                                <div className="upload-card-title">Keywords File</div>
                                <div className="upload-card-desc">CSV with 'Keyword', 'Match Type', 'Bid'. One campaign per keyword.</div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">{keywordsFile ? 'Change File' : 'Select File'}</div>
                                    <input type="file" accept=".csv" onChange={handleKeywordsFileChange} hidden />
                                </label>
                                {keywordsFile && <small className="text-success mt-2">{keywordsFile.name}</small>}
                            </div>

                            {/* ASINs */}
                            <div className={`upload-card-new ${productsFile ? 'has-file' : ''}`}>
                                <div className="card-required-badge">Required</div>
                                <div className="upload-icon"><FaFileAlt /></div>
                                <div className="upload-card-title">ASINs File</div>
                                <div className="upload-card-desc">CSV with at least 3 ASINs</div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">{productsFile ? 'Change File' : 'Select File'}</div>
                                    <input type="file" accept=".csv" onChange={handleProductsFileChange} hidden />
                                </label>
                                {productsFile && <small className="text-success mt-2">{productsFile.name}</small>}
                            </div>

                            {/* Logo */}
                            <div className={`upload-card-new ${logoFile ? 'has-file' : ''}`}>
                                <div className="card-required-badge">Required</div>
                                <div className="upload-icon">
                                    {logoPreview ? <img src={logoPreview} alt="Logo" style={{ maxHeight : '50px' }} /> : <FaImage />}
                                </div>
                                <div className="upload-card-title">Brand Logo</div>
                                <div className="upload-card-desc">JPG or PNG, recommended 400x400px</div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">{logoFile ? 'Change File' : 'Select File'}</div>
                                    <input type="file" accept="image/*" onChange={handleLogoChange} hidden />
                                </label>
                                {logoFile && <small className="text-success mt-2">{logoFile.name}</small>}
                            </div>

                            {/* Creative */}
                            <div className={`upload-card-new ${creativeFile ? 'has-file' : ''}`}>
                                <div className="upload-icon">
                                    {creativePreview ? <img src={creativePreview} alt="Creative" style={{ maxHeight : '50px' }} /> : <FaImage />}
                                </div>
                                <div className="upload-card-title">Creative (Optional)</div>
                                <div className="upload-card-desc">Image/Video. Amazon can generate it automatically.</div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">{creativeFile ? 'Change File' : 'Select File'}</div>
                                    <input type="file" accept="image/*,video/*" onChange={handleCreativeChange} hidden />
                                </label>
                                {creativeFile && <small className="text-success mt-2">{creativeFile.name}</small>}
                            </div>

                            {/* Negative */}
                            <div className={`upload-card-new ${negativeFile ? 'has-file' : ''}`}>
                                <div className="upload-icon"><FaFileAlt /></div>
                                <div className="upload-card-title">Negative Targeting (Optional)</div>
                                <div className="upload-card-desc">Columns: Abbreviation, Negative Targeting, Match Type</div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">{negativeFile ? 'Change File' : 'Select File'}</div>
                                    <input type="file" accept=".csv" onChange={handleNegativeFileChange} hidden />
                                </label>
                                {negativeFile && <small className="text-success mt-2">{negativeFile.name}</small>}
                            </div>
                        </div>

                        {/* Generate Button */}
                        <div className="generate-section">
                            <button className={`btn-generate-campaigns ${(!processingFiles && !loading) ? 'ready' : ''}`} onClick={handleGenerateClick} disabled={processingFiles || loading}>
                                {processingFiles ? <><Spinner size="sm" animation="border" className="me-2" /> Processing...</> : <><FaRocket /> Generate Campaigns (1 per keyword)</>}
                            </button>
                        </div>

                        {/* Requirements */}
                        <div className="requirements-section">
                            <span className="requirements-title">Requirements - Single Keyword Campaigns</span>
                            <div className="req-item"><strong>Keywords CSV:</strong> Each keyword creates an individual campaign.</div>
                            <div className="req-item"><strong>ASINs CSV:</strong> Requires 3-10 ASINs. Used across all campaigns.</div>
                            <div className="req-item"><strong>Logo:</strong> Required. Same logo for all campaigns.</div>
                            <div className="req-item"><strong>Headline:</strong> Optional. If not provided, the keyword is used as headline.</div>
                        </div>
                    </>
                )}

                {/* Step 2: Editor */}
                {step === 2 && (
                    <Row>
                        <Col md={12}>
                            <Card className="config-section-card border-0 p-0 overflow-hidden">
                                <Card.Header className="bg-dark text-white p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5><FaRocket className="me-2" /> Campaign Editor ({generatedCampaigns.length})</h5>
                                        <div className="d-flex gap-2">
                                            <Button variant="outline-danger" size="sm" onClick={handleDeleteSelected} disabled={selectedCampaignIndices.size === 0}>
                                                <FaTrash className="me-1" /> Delete Sel.
                                            </Button>
                                            <Button variant="warning" onClick={handleExportCSV} className="fw-bold text-dark">
                                                <FaDownload className="me-1" /> Export CSV
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <strong className="text-white me-2">Bulk actions:</strong>
                                        <Form.Select size="sm" style={{ width: '200px' }} value={bulkAction} onChange={e => setBulkAction(e.target.value)}>
                                            <option value="">Select action</option>
                                            <option value="PAUSED">Change to Paused</option>
                                            <option value="ENABLED">Change to Enabled</option>
                                        </Form.Select>
                                        <Button variant="warning" size="sm" onClick={handleBulkActionApply} className="text-dark fw-bold">Apply</Button>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0 table-responsive bg-dark" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                    <Table hover variant="dark" className="align-middle mb-0" style={{ fontSize: '0.85rem' }}>
                                        <thead className="text-warning sticky-top bg-dark">
                                            <tr>
                                                <th width="40"><Form.Check type="checkbox" onChange={handleSelectAll} /></th>
                                                <th>Keyword</th>
                                                <th>Match Type</th>
                                                <th width="70">Bid</th>
                                                <th width="80">Budget</th>
                                                <th width="250">Campaign Name</th>
                                                <th width="200">Headline</th>
                                                <th>State</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {generatedCampaigns.map((camp, idx) => (
                                                <tr key={idx}>
                                                    <td><Form.Check type="checkbox" checked={selectedCampaignIndices.has(idx)} onChange={() => handleSelectRow(idx)} /></td>
                                                    <td className="text-info fw-bold">{camp.keywordText}</td>
                                                    <td><Badge bg="secondary">{camp.matchType}</Badge></td>
                                                    <td><Form.Control type="number" size="sm" value={camp.bid} onChange={e => updateCampaignRow(idx, 'bid', e.target.value)} className="form-control-dark p-1" /></td>
                                                    <td><Form.Control type="number" size="sm" value={camp.budget} onChange={e => updateCampaignRow(idx, 'budget', e.target.value)} className="form-control-dark p-1" /></td>
                                                    <td><Form.Control type="text" size="sm" value={camp.name} onChange={e => updateCampaignRow(idx, 'name', e.target.value)} className="form-control-dark p-1" /></td>
                                                    <td><Form.Control type="text" size="sm" value={camp.headline} onChange={e => updateCampaignRow(idx, 'headline', e.target.value)} maxLength={50} className="form-control-dark p-1" /></td>
                                                    <td><StatusBadge status={camp.status} /></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                                <Card.Footer className="bg-dark border-top border-secondary d-flex justify-content-between p-3">
                                    <Button variant="secondary" onClick={() => setStep(1)} className="fw-bold">Back</Button>
                                    <div className="d-flex gap-3 align-items-center">
                                        <span className="text-muted">Total: {generatedCampaigns.length} | Sel: {selectedCampaignIndices.size}</span>
                                        <Button variant="success" onClick={handleCreateAllCampaigns} disabled={loading} className="fw-bold">
                                            {loading ? <Spinner animation="border" size="sm" /> : (selectedCampaignIndices.size > 0 ? 'Create Selected' : 'Create All')}
                                        </Button>
                                    </div>
                                </Card.Footer>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Step 3: Results */}
                {step === 3 && result && (
                    <Row>
                        <Col md={10} className="mx-auto">
                            <Card className="bg-dark text-white border-0">
                                <Card.Header className="bg-success text-white">
                                    <FaCheckCircle className="me-2" /> Execution Complete
                                </Card.Header>
                                <Card.Body>
                                    <Table variant="dark" striped bordered hover>
                                        <thead>
                                            <tr><th>Campaign</th><th>Status</th><th>Details</th></tr>
                                        </thead>
                                        <tbody>
                                            {result.results && result.results.map((res, i) => (
                                                <tr key={i}>
                                                    <td>{res.name}</td>
                                                    <td>{res.success ? <Badge bg="success">Created</Badge> : <Badge bg="danger">Failed</Badge>}</td>
                                                    <td>{res.success ? <small className="text-muted">ID : {res.id}</small> : <small className="text-danger">{res.message}</small>}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                    <div className="d-flex justify-content-end mt-3">
                                        <Button variant="primary" onClick={() => window.location.reload()}>Create More</Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}
            </Container>
        </Layout>
    );
};

export default SbSingleKeywordCampaigns;

